import { FetchResult, useApolloClient, useMutation } from '@apollo/client'
import { useState } from 'react'
import { getSurveyOrderSoftLaunchRefetchQuery } from '../../../components/SoftLaunch'
import {
  AddPanelSupplierOrderMutationVariables,
  FullLaunchDocument,
  FullLaunchMutation,
  FullLaunchMutationVariables
} from '../../../data/gql-gen/dashboard/graphql'
import { LoggerErrorType } from '../../../data/logger'
import { captureApolloError } from '../../../utils/HelperFunctions'
import { PanelSupplier } from '../../../utils/audienceHelper'
import { PROJECT_BY_SURVEY_ID } from '../../Project/Project.query'
import { usePauseResumeSurvey } from '../../Survey/usePauseResumeSurvey'
import {
  ADD_PANEL_SUPPLIER_ORDER,
  AddPanelSupplierOrderData
} from './FullLaunchButton.mutations'

export interface UseFullLaunchInput {
  surveyId: string
  /** Supplier of the already-placed soft-launch order. */
  panelSupplierCode?: string
  /** Full (adjusted) fieldwork target. */
  samplesNeeded: number
  /** Full target minus the soft-launch completes. */
  remainingSamplesNeeded: number
  /** Paused survey — the launch resumes it, or it collects nothing. */
  isPaused?: boolean
  /** Runs when the launch order lands (before any follow-up refetch settles). */
  onCompleted?: () => void | Promise<unknown>
}

/**
 * Promote a soft-launched survey to a full launch. Extracted from
 * FullLaunchButton.container so the multi-market Confirm page can launch any
 * market by surveyId; the caller supplies the order/fieldwork numbers from
 * whichever query it already runs. Supplier semantics differ and must be kept
 * as-is:
 * - CINT: one additional supplier order for the remaining completes.
 * - PURESPECTRUM: `fullLaunch` re-targets the existing order to the full
 *   target, then a nominal 2-sample supplier order is added — its real purpose
 *   is creating the second panelSupplierOrder that marks the survey as
 *   full-launched (isFullLaunchOrder = orders.length > 1).
 * - Anything else: `fullLaunch` with the remaining completes.
 */
export const useFullLaunch = ({
  surveyId,
  panelSupplierCode,
  samplesNeeded,
  remainingSamplesNeeded,
  isPaused = false,
  onCompleted
}: UseFullLaunchInput) => {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [isOrderPlaced, setIsOrderPlaced] = useState(false)

  const handleCompleted = () => {
    setIsOrderPlaced(true)
    setIsPlacingOrder(false)
    void onCompleted?.()
  }
  const handleFailed = () => {
    setIsOrderPlaced(false)
    setIsPlacingOrder(false)
  }

  const client = useApolloClient()
  const { resume } = usePauseResumeSurvey(() =>
    client.refetchQueries({ include: [PROJECT_BY_SURVEY_ID] })
  )

  // These mutations resolve rather than reject (they have an `onError`), so the
  // payload is what says the launch landed — a failed one must not resume.
  const resumeIfLaunched = async (
    result: FetchResult<unknown> | null | undefined
  ) => {
    if (isPaused && result?.data && !result.errors?.length) {
      await resume(surveyId)
    }
  }

  const [addPanelSupplierOrder, { loading: loadingAddPanelSupplierOrder }] =
    useMutation<
      AddPanelSupplierOrderData,
      AddPanelSupplierOrderMutationVariables
    >(ADD_PANEL_SUPPLIER_ORDER, {
      context: { clientName: 'dashboard' },
      onCompleted: handleCompleted,
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'addPanelSupplierOrder',
          error
        )
        handleFailed()
      },
      refetchQueries: [getSurveyOrderSoftLaunchRefetchQuery(surveyId)]
    })

  const [fullLaunch, { loading: loadingFullLaunch }] = useMutation<
    FullLaunchMutation,
    FullLaunchMutationVariables
  >(FullLaunchDocument, {
    context: { clientName: 'dashboard' },
    onCompleted: handleCompleted,
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloMutation, 'fullLaunch', error)
      handleFailed()
    },
    refetchQueries: [getSurveyOrderSoftLaunchRefetchQuery(surveyId)]
  })

  const confirmFullLaunch = () => {
    setIsPlacingOrder(true)

    if (panelSupplierCode === PanelSupplier.CINT) {
      addPanelSupplierOrder({
        variables: {
          surveyId,
          orderDescription: 'Full launch',
          samplesNeeded: remainingSamplesNeeded,
          panelSupplierCode: PanelSupplier.CINT
        }
      })
        .then(resumeIfLaunched)
        .catch(handleFailed)
    } else if (panelSupplierCode === PanelSupplier.PURESPECTRUM) {
      // For PureSpectrum, first call fullLaunch, then add the panel supplier
      // order after completion.
      fullLaunch({
        variables: {
          surveyId,
          orderDescription: 'Full launch',
          samplesNeeded
        }
      })
        .then(async (result) => {
          // Resume off `fullLaunch` — the marker order below is nominal, and
          // its failing must not leave the survey paused.
          await resumeIfLaunched(result)
          // After fullLaunch completes, add panel supplier order with 2 samples
          await addPanelSupplierOrder({
            variables: {
              surveyId,
              orderDescription: 'Full launch',
              samplesNeeded: 2,
              panelSupplierCode: PanelSupplier.PURESPECTRUM
            }
          })
        })
        .catch(handleFailed)
    } else {
      fullLaunch({
        variables: {
          surveyId,
          orderDescription: 'Full launch',
          samplesNeeded: remainingSamplesNeeded
        }
      })
        .then(resumeIfLaunched)
        .catch(handleFailed)
    }
  }

  return {
    confirmFullLaunch,
    isPlacingOrder:
      isPlacingOrder || loadingAddPanelSupplierOrder || loadingFullLaunch,
    isOrderPlaced
  }
}
